// Percorso file: src/pages/legal/legalDocs.js
//
// I DOCUMENTI LEGALI DELL'APP — sede unica dei DATI (cantiere LEGAL-DOCS).
//
// Qui non c'è nessun disegno: solo i dati, come in components/clubLinks.js
// («Qui non c'è nessun disegno: solo i dati. Il markup … resta nelle
// pagine»). La resa a schermo sta in LegalDocument.jsx (il documento) e
// LegalIndex.jsx (gli elenchi per categoria). Il file è `.js` e non `.jsx`
// di proposito: non contiene JSX e non importa React.
//
// LA STRUTTURA REGGE PIÙ DOCUMENTI E PIÙ CATEGORIE: `LEGAL_DOCS` è una
// mappa slug → documento, e ogni documento porta una `category`. Gli
// elenchi si costruiscono da `Object.values(LEGAL_DOCS)` filtrando per
// categoria: /legal mostra i documenti "legal" (oggi il Privacy Statement),
// /terms i documenti "terms" (i cinque documenti del club). Lo SLUG È UNICO
// FRA TUTTI I DOCUMENTI, di qualunque categoria, perché il visualizzatore
// è uno solo (/legal/<slug>): la categoria vive nel dato, non nell'URL. Un
// documento nuovo si aggiunge qui e compare da sé nell'elenco della sua
// categoria e alla rotta /legal/<slug>, senza toccare le pagine.
//
// Forma di un documento:
//   slug               — chiave della mappa E segmento della rotta
//                        (/legal/<slug>); i due devono coincidere;
//   category           — "legal" (Legal & Privacy) o "terms" (Terms &
//                        Conditions): decide in quale elenco compare;
//                        "safety" (dal 14/9/2026, fetta MPIO) NON ha un
//                        elenco: la voce «Member Protection» di MorePage.jsx
//                        apre direttamente /legal/mpio;
//   title / shortTitle — titolo a schermo (h1 e voce d'elenco) e forma breve;
//   version            — etichetta di versione (⚪ «Doc.NN-NN» è la
//                        convenzione del riferimento: la conferma il
//                        committente);
//   status             — "draft" finché manca la verifica (legale per il
//                        Privacy Statement; contro l'originale Word per i
//                        cinque documenti del club); è un dato INTERNO:
//                        dal cantiere LEGAL-NODRAFT (14/9/2026) il renderer
//                        NON lo dichiara più a schermo;
//   effectiveDateLabel — la decorrenza, GIÀ FORMATTATA per lo schermo
//                        (il renderer la mostra tale e quale, senza
//                        prefissi); vuota ("") quando il documento non ha
//                        una data: allora il renderer non stampa neppure il
//                        separatore dopo la versione;
//   legalEntity / legalEntityId / contactEmail — la testata;
//   sections           — le sezioni, in ordine: { id, label, body }, dove
//                        `id` è l'ancora dell'<h2>, `label` il suo testo e
//                        `body` la sequenza ORDINATA dei blocchi del corpo:
//                          { type: "p",  text: "…" }        → un paragrafo
//                          { type: "ul", items: [ … ] }     → una lista puntata
//                        Una VOCE di lista è una stringa, oppure — se ha
//                        sotto-voci — un oggetto { text, items } dove
//                        `items` ha di nuovo la stessa forma (ricorsivo,
//                        fino a quattro livelli nel riferimento). La
//                        NUMERAZIONE del club (1. / a. / i.) STA DENTRO il
//                        testo della voce: il documento la usa per i
//                        rimandi interni («Where 4[b] occurs», «as per
//                        #6»), quindi non si toglie e non si delega al
//                        browser. Le liste sono liste e non paragrafi
//                        appiattiti: il riferimento le ha, e lo schermo le
//                        mostra.
//
// ⚠️ QUESTO FILE È GENERATO dai riferimenti congelati (fuori git)
// docs/cantieri/legal-docs/CONTENT-B1.md (il Privacy Statement) e
// docs/cantieri/legal-docs/CONTENT-TERMS.md (i cinque documenti del club) e
// docs/cantieri/legal-docs/CONTENT-MPIO.md (la scheda del MPIO):
// le stringhe sono state COPIATE, non ribattute, con la stessa disciplina
// di enrolLingue.js. Regola dei riferimenti: header «## <id> | <label>»,
// righe normali = paragrafi, righe che iniziano con «- » = voci di una
// lista (consecutive = una lista sola), e l'INDENTAZIONE della voce
// («  - » = secondo livello, «    - » = terzo, …) è l'annidamento. Una
// correzione al testo si fa PRIMA nel riferimento e POI si rigenera qui:
// non si corregge, non si abbrevia, non si riformula in questo file. Vale
// anche per le note «[VERIFICA: …]» lasciate nel testo dal riferimento:
// sono per il committente, e restano finché lui non le chiude lì.
//
// Il testo è in inglese e SOLO in inglese (CLAUDE.md §3.7; enrolLingue.js:
// «le POLICY … restano in inglese in ogni lingua — sono la formula legale»).

export const LEGAL_DOCS = {
  privacy: {
    slug: "privacy",
    category: "legal",
    title: "Privacy Statement",
    shortTitle: "Privacy",
    version: "Doc.06-01",
    status: "draft",
    effectiveDateLabel: "",
    legalEntity: "Queens Park Soccer Club Inc.",
    legalEntityId: "ABN 35 060 829 551",
    contactEmail: "footballdepartment@queensparksc.com.au",
    sections: [
      {
        id: "who-this-is-for",
        label: "1. Who this statement is for",
        body: [
          { type: "p", text: "This statement explains, in plain language, what personal information Queens Park Soccer Club Inc. (\"QPSC\", \"the club\", \"we\", \"us\") collects through the QPSC Club App, why we collect it, who can see it, and what you can do about it." },
          { type: "p", text: "It is written for two readers at once:" },
          {
            type: "ul",
            items: [
              "\"Your information\" — if you have your own account (an adult player, a coach, or a parent/guardian), this is about you.",
              "\"Your child's information\" — if you are a parent or guardian, you hold your child's record in the app. Your child does not have their own account. Where the rules for a child are different, we say so under a \"For your child\" note.",
            ],
          },
          { type: "p", text: "We handle personal information in line with the Privacy Act 1988 (Cth) and the Australian Privacy Principles (APPs)." },
        ],
      },
      {
        id: "who-we-are",
        label: "2. Who we are",
        body: [
          { type: "p", text: "Queens Park Soccer Club Inc." },
          { type: "p", text: "Coker Park, 181 Wharf Street, Queens Park WA 6107" },
          { type: "p", text: "ABN 35 060 829 551 · IARN A0790133D" },
          { type: "p", text: "For any privacy question, or to ask for access, correction or deletion of your information, contact the Football Department at footballdepartment@queensparksc.com.au." },
        ],
      },
      {
        id: "what-we-collect",
        label: "3. What we collect",
        body: [
          { type: "p", text: "We collect the information you give us when you enrol and use the app:" },
          {
            type: "ul",
            items: [
              "About the member — name, date of birth, gender, country of birth, nationality, and your PlayFootball / Football Australia (FFA) number.",
              "Contact and address — email, phone, and postal address.",
              "Emergency contacts — the name, phone and email of the people you list to be contacted in an emergency.",
              "A photo, if you upload one (used as the profile picture inside the app).",
              "An identity document, if you choose to provide one (not required to use the app).",
              "Membership and activity — the team(s) you belong to, attendance, game call-ups, uniform allocation, and season records.",
              "Payments — the fees due, discounts, and a record of payments made.",
              "Messages — what you write in the club's in-app chat.",
            ],
          },
          { type: "p", text: "Some of this (for example nationality or country of birth) may be sensitive information under the Privacy Act. We collect it only because parts of it are needed to register players with Football Australia and Football West, and we handle it with the same care as the rest of your information." },
          { type: "p", text: "For your child: the same list applies to the child's record, which the parent or guardian fills in and keeps up to date on the child's behalf." },
        ],
      },
      {
        id: "how-we-use",
        label: "4. Why we use it",
        body: [
          { type: "p", text: "We use this information only to run the club: to place members in teams, take attendance, manage game call-ups, calculate and record fees, allocate uniforms, communicate with members through the app, and keep members safe. We do not use it for advertising, and we do not sell it." },
        ],
      },
      {
        id: "who-can-see",
        label: "5. Who can see it",
        body: [
          { type: "p", text: "Access inside the app is limited by role, and enforced on our server — not just hidden on the screen:" },
          {
            type: "ul",
            items: [
              "Administrators can see all members' information, because they run the club.",
              "Every member can see their own information.",
              "A coach can see only the players in their own team(s).",
              "A parent/guardian can see only their own children's records.",
            ],
          },
          { type: "p", text: "There are no public profiles. Nothing in the app is visible to the general public or to search engines. A member's information is never shown to other members outside the rules above." },
        ],
      },
      {
        id: "disclosure",
        label: "6. Who we share it with",
        body: [
          { type: "p", text: "To run the club we share the information we have to, and no more:" },
          {
            type: "ul",
            items: [
              "Football Australia (PlayFootball) and Football West — to register players and enter teams in competition.",
              "City of Canning — for ground and facility arrangements.",
              "Square and Afterpay — to process payments, if you pay online.",
              "Your child's school — only if you ask for a school-based discount, and only to confirm enrolment.",
              "Google / Firebase — the technology that hosts the app (see Section 7).",
            ],
          },
          { type: "p", text: "Australia Goal. Some team pages show an \"Australia Goal\" button that links out to a separate app run by another company. If you tap it you leave the QPSC app, and from that point their own privacy terms apply. No QPSC member information — and no information about a child — is sent to Australia Goal. The link only goes outward." },
        ],
      },
      {
        id: "storage-security",
        label: "7. Where it's stored and how it's protected",
        body: [
          { type: "p", text: "Your information is stored in Australia (Google Firebase, Sydney region). You sign in through a secure link sent to your email — the club does not store account passwords. Access is controlled by the role rules in Section 5." },
        ],
      },
      {
        id: "cookies",
        label: "8. Cookies and similar technology",
        body: [
          { type: "p", text: "The app uses only the technology strictly needed to keep you signed in and to make the app work. We do not use analytics, tracking or marketing cookies, so there is nothing here for you to switch on or off." },
        ],
      },
      {
        id: "retention",
        label: "9. How long we keep it",
        body: [
          { type: "p", text: "We keep a minimal sporting record — the member's name, the teams they played for, and the seasons they played — as part of the club's history, for as long as the club exists." },
          { type: "p", text: "Everything else is deleted or de-identified once you are no longer an active member — in practice, after the end of the season following your last active season." },
          { type: "p", text: "The one exception is information we are required by law to keep: in particular payment and financial records, which we keep for the period Australian tax law requires (generally at least five years), and any records needed while an insurance, safety or legal matter is still open. Once those periods pass, that information is deleted too." },
        ],
      },
      {
        id: "your-rights",
        label: "10. Your rights",
        body: [
          { type: "p", text: "You can ask us to:" },
          {
            type: "ul",
            items: [
              "see the information we hold about you;",
              "correct anything that is wrong; and",
              "delete your information.",
            ],
          },
          { type: "p", text: "Just email footballdepartment@queensparksc.com.au. We will act on your request unless the law requires us to keep something (see Section 9), in which case we will tell you." },
          { type: "p", text: "For your child: as the parent or guardian, you make these requests on your child's behalf." },
        ],
      },
      {
        id: "children",
        label: "11. Children's privacy",
        body: [
          { type: "p", text: "Protecting children's information is built into how the app works, not added on top:" },
          {
            type: "ul",
            items: [
              "A child does not have their own account. A parent or guardian enrols the child and holds the child's record. This means a parent's consent is required for a child to be in the club at all.",
              "A child's information has no public profile and is never visible to the public. Inside the app it can be seen only by administrators, by the child's own parent(s)/guardian(s), and by the coach of the child's team.",
              "A parent or guardian can see, correct or delete their child's information at any time, by emailing the Football Department.",
              "We are following the Children's Online Privacy Code being introduced by the Office of the Australian Information Commissioner (OAIC) under the Privacy and Other Legislation Amendment Act 2024 (Cth), expected to take effect by 10 December 2026, and we will align the app with it.",
            ],
          },
        ],
      },
      {
        id: "changes",
        label: "12. Changes to this statement",
        body: [
          { type: "p", text: "We may update this statement from time to time. The current version is always the one shown in the app, and the version number and date at the top tell you which one it is. If we make an important change, we will let members know by email." },
        ],
      },
      {
        id: "complaints",
        label: "13. Complaints",
        body: [
          { type: "p", text: "If you have a concern about how we handle your information, contact the Football Department first at footballdepartment@queensparksc.com.au — we will try to resolve it with you. If you are not satisfied, you can contact the Office of the Australian Information Commissioner (OAIC) at oaic.gov.au." },
        ],
      },
    ],
  },
  "membership-policy": {
    slug: "membership-policy",
    category: "terms",
    title: "Membership Policy",
    shortTitle: "Membership",
    version: "Doc.01-02",
    status: "draft",
    effectiveDateLabel: "dated 07/09/2026",
    legalEntity: "Queens Park Soccer Club Inc.",
    legalEntityId: "ABN 35 060 829 551",
    contactEmail: "footballdepartment@queensparksc.com.au",
    sections: [
      {
        id: "membership-payments",
        label: "1. Membership payment/s",
        body: [
          { type: "p", text: "The participant agrees to make the membership payment/s for football [soccer] training and games to Queens Park SC [QPSC] with the following deadlines:" },
          {
            type: "ul",
            items: [
              {
                text: "1. The membership fee includes:",
                items: [
                  "Weekly training sessions",
                  "Football West competition",
                  "Football Australia Fees & Insurance",
                  "Football West Fees",
                  "QPSC club Fees",
                  "Queens Park SC uniform",
                  "City of Canning Fees & charges",
                  "PlayFootball registration [players from 5 to 18 years of age in membership season]",
                ],
              },
              "2. Enrolment and payment is mandatory via the QPSC Club App [payment option/s will be emailed at the time of enrolment].",
              {
                text: "3. There are two [2] different type of payments:",
                items: [
                  {
                    text: "a. Full amount via:",
                    items: [
                      "i. Cash at the Club",
                      "ii. Bank Transfer",
                      "iii. EFTPOS at the Club [1% transaction fee]",
                      "iv. Online Payment link using Square [1.6% transaction fee]",
                    ],
                  },
                  "b. Payment plan via Square checkout by selecting Afterpay you can make payment over 4 equal payments.",
                ],
              },
              {
                text: "4. Special conditions may be possible for an approved applicant for a payment plan arrangement [will incur a 10% additional administration charge]. To be eligible MUST be a family with three [3] or more siblings. Requests must be submitted in writing to the QPSC Football Department at the email footballdepartment@queensparksc.com.au.",
                items: [
                  "a. All QPSC payment plans MUST be completed before 1 July of the Membership year.",
                  {
                    text: "b. Failure to comply with QPSC payment schedule will result in immediate SUSPENSION of the member/player/s from all QPSC activities and cancellation of payment plan.",
                    items: [
                      "i. Where 4[b] occurs member can clear suspension and be reinstated by payment of ALL outstanding fees plus 10% late payment penalty fee.",
                    ],
                  },
                ],
              },
              {
                text: "5. Membership Discount/s",
                items: [
                  {
                    text: "a. Early Bird enrolment - members who join prior to 1 January of the membership season may receive a reduce or Early Bird discount. Requires;",
                    items: [
                      "i. Deposit at time of enrolment plus payment of remaining fees by 31 January in membership year.",
                    ],
                  },
                  {
                    text: "b. Family [siblings] - members aged from 5 years to 18 years old.",
                    items: [
                      "i. 10% Discount for two [2] siblings.",
                      "ii. 20% Discount for three [3] or more siblings.",
                    ],
                  },
                  {
                    text: "c. School",
                    items: [
                      {
                        text: "i. If your child is currently enrolled as a student at the following schools listed below;",
                        items: [
                          "1. Cannington Community College",
                          "2. Gibb St Primary",
                          "3. Sevenoaks Senior College",
                          "4. St.Norbert College",
                        ],
                      },
                      "ii. To apply this discount, so that QPSC can amend your fees accordingly please email our Club Registrar at footballdepartment@queensparksc.com.au and advise which school you are enrolled.",
                      "iii. Confirmation requests will be sent to the school.",
                    ],
                  },
                ],
              },
            ],
          },
          { type: "p", text: "In cases where the payment is overdue on more than one (1) occasion, QPSC reserves the right to cancel payment plan, suspend membership and request full payment. Suspended membership would only become \"ACTIVE\" once requested full payment made. [VERIFICA: nel PDF questa frase inizia con \"MUSTIn cases\" — probabile artefatto di estrazione, qui reso \"In cases\"; conferma sull'originale Word.]" },
        ],
      },
      {
        id: "uniforms",
        label: "2. Uniforms",
        body: [
          { type: "p", text: "The participant agrees to wear the QPSC uniform at all times during training sessions or matches. Respect for the uniform must be upheld, and it should be kept in good condition and clean." },
          { type: "p", text: "Upon enrolment you will be advised when you can attend the clubrooms to be sized and collect relevant uniform." },
        ],
      },
      {
        id: "commitment",
        label: "3. Commitment to Participation",
        body: [
          { type: "p", text: "The participant understands that the membership grants the right to participate in scheduled football [soccer] training sessions and games. It is the participant's responsibility to attend these sessions punctually." },
          { type: "p", text: "QPSC is NOT a private training academy, we are a club who organise teams to represent QPSC in Football West competitions run throughout the membership period. Players who DO NOT attend 75% of scheduled match days may not be offered a position at the club the following year." },
        ],
      },
      {
        id: "absence",
        label: "4. Absence or Cancellation",
        body: [
          { type: "p", text: "The participant acknowledges that the fees cover all sessions during the year and not attending is a personal choice and no refunds will be issued for training sessions they do not attend or that are cancelled." },
        ],
      },
      {
        id: "sessions-cancelled",
        label: "5. Training Sessions Cancelled",
        body: [
          { type: "p", text: "In the event of training sessions being cancelled by QPSC for any reason, including field issues or In the case of adverse weather conditions or force majeure situations, the participant understands that no refunds will be due for sessions not conducted. Sessions may be rescheduled from time to time." },
        ],
      },
      {
        id: "compliance",
        label: "6. Compliance with Rules and Regulations",
        body: [
          { type: "p", text: "The participant commits to abide by all rules, regulations, and norms established by QPSC for participation in the training sessions and games. Non-compliance with these rules may result in expulsion from the program without the right to a refund." },
        ],
      },
      {
        id: "waiver",
        label: "7. Waiver of Liability",
        body: [
          { type: "p", text: "The participant acknowledges that participating in the training sessions or games carries risks of injury and agrees to waive any legal claims against QPSC, coaches, and staff in the event of injuries or damages incurred during participation in the training sessions or games." },
        ],
      },
      {
        id: "changes",
        label: "8. Changes to Terms",
        body: [
          { type: "p", text: "QPSC reserves the right to make changes to these terms at any time, without prior notice to the participant. [However the current Membership Policy will be available on the clubs website for all members]" },
          { type: "p", text: "By enrolling and/or registering with QPSC the participant or parent is accepting that they have read, agreed and understand the above clauses, to the terms for membership payment/s for football [soccer] training and games." },
          { type: "p", text: "Kind regards, Queens Park SC Inc., Executive Committee." },
        ],
      },
    ],
  },
  "parent-code-of-conduct": {
    slug: "parent-code-of-conduct",
    category: "terms",
    title: "Parent Code of Conduct",
    shortTitle: "Parent Code of Conduct",
    version: "Doc.02-02",
    status: "draft",
    effectiveDateLabel: "dated 06/08/2026",
    legalEntity: "Queens Park Soccer Club Inc.",
    legalEntityId: "ABN 35 060 829 551",
    contactEmail: "footballdepartment@queensparksc.com.au",
    sections: [
      {
        id: "code-of-conduct",
        label: "Parent Code of Conduct",
        body: [
          {
            type: "ul",
            items: [
              "1. Positive Support: Encourage your child, their teammates, and opposition in a positive manner, establishing a supportive and productive environment.",
              "2. Respect for Officials: During the game, No criticising or challenging the decisions made by referees, linesmen, or any other officials or volunteers.",
              "3. Model Sportsmanship: By appreciating players and officials from both teams, fostering fair play, and avoiding unpleasant comments.",
              "4. Player Development Focus: Prioritise player growth and skill improvement over a sole focus on winning or losing.",
              "5. Avoid Sideline Coaching: Parents are NOT Coach from the sidelines. ONLY our appointed coaches are to instruct players during the game to reduce player confusion in the game.",
              "6. Welfare Communication: Address any concerns about your child's welfare or development by communicating directly with the clubs Football Department (FD) at footballdepartment@queensparksc.com.au.",
              "7. Social Media Etiquette: DO NOT use social media to criticise the club, its officials, or any individuals linked with the team or games played. As per #6 contact our FD if you have any concerns.",
              "8. Financial Responsibility: Accept personal responsibility for any financial liabilities incurred by the club as a result of events involving your child and yourself, as a parent, or guardian.",
              "9. Consent for Media: Obtain permission before posting any images or information about the team or players on social media or other public venues.",
              "10. Suspension/Removal: Parents, other family members and spectators may be asked to leave the venue and/or not allowed to attend any other match, involving players team. In Serious and/or repeated cases, may be asked to leave the club (with their child/ren) if they engage in inappropriate behaviours. Ie. Arguing with team officials of QPSC or opposition clubs and supporters, repeatedly disputing our coaches' decisions during matches, etc.",
              "11. Decisions are Final and Binding: Understand and agree that decisions made by the Club's Management are final.",
            ],
          },
          { type: "p", text: "Note: Your agreement to this Parent CoC is established when submitting your child's registration via the PlayFootball system." },
        ],
      },
    ],
  },
  "parents-expectations": {
    slug: "parents-expectations",
    category: "terms",
    title: "Parents Expectations",
    shortTitle: "Parents Expectations",
    version: "Doc.03-02",
    status: "draft",
    effectiveDateLabel: "dated 03/08/2026",
    legalEntity: "Queens Park Soccer Club Inc.",
    legalEntityId: "ABN 35 060 829 551",
    contactEmail: "footballdepartment@queensparksc.com.au",
    sections: [
      {
        id: "expectations",
        label: "Parents Expectations",
        body: [
          {
            type: "ul",
            items: [
              "1. I agree to observe all the Queens Park SC (QPSC) policies, as well as the Football Australia (FFA) and Football West (FW) rules and regulations.",
              "2. I realise the significance of not interfering with the coaching staff and the Technical Director (TD) during training sessions and/or games. This includes not coaching or shouting directions during training or games.",
              "3. Any concerns I have for coaches or team staff should be sent to the Football Department (FD) at footballdepartment@queensparksc.com.au",
              "4. I shall not enter the allocated training area or game day pitch during training and/or games. During these times, coaches will not engage in discussions about player or parent concerns. Please refer to #3 above.",
              "5. To fulfil the function of a club assistant referee (linesperson) during all games, I shall stick to the roster specified by the Team Manager. This includes pre-season and FW sanctioned games, with the understanding that the game cannot begin unless a linesperson is present. It is my obligation to locate an appropriate replacement if I am unable to attend on my rostered day.",
              "6. For HOME games, I will help with pitch set-up and pack-away (by moving goals if needed and put-up nets and corner flags before kick-off and assist in taking them down afterward). Compliance with the Team Manager's requests is required for games to begin on schedule. Player will be completing their warm-ups or cool down with the coach and not available to assist with this.",
              "7. For HOME games, I understand if NO official FW referee attends that I will be required to fulfil this function if rostered as linesperson, and the next rostered linesperson will need to complete the linesperson duties so the game can take place (you will be advised by the Team Manager). This includes pre-season and FW sanctioned games, with the understanding that the game cannot begin unless a referee & linesperson is present. If you're unable to attend on your rostered day, it is your responsibility to swap with another parent.",
              "8. For HOME games, I understand that to fulfil the function of a Ground Marshal during our game, I shall stick to the roster specified by the Team Manager. This includes pre-season and FW sanctioned games, with the understanding that the game may not begin unless a Ground Marshal is present. It is my obligation to locate an appropriate replacement if I am unable to attend on my roster.",
              "9. If there are any holidays or absences during the season, I will notify the Team Manager/Coach as soon as possible so that they may plan accordingly.",
              "10. Any information, feedback, or questions about QPSC should be sent to the FD at footballdepartment@queensparksc.com.au.",
              "11. Communication via social media outlets is not permitted.",
            ],
          },
          { type: "p", text: "Note: Your agreement to this Parent Expectations is established when submitting your child's registration via the PlayFootball system." },
        ],
      },
    ],
  },
  "player-code-of-conduct": {
    slug: "player-code-of-conduct",
    category: "terms",
    title: "Player Code of Conduct",
    shortTitle: "Player Code of Conduct",
    version: "Doc.04-02",
    status: "draft",
    effectiveDateLabel: "dated 06/08/2026",
    legalEntity: "Queens Park Soccer Club Inc.",
    legalEntityId: "ABN 35 060 829 551",
    contactEmail: "footballdepartment@queensparksc.com.au",
    sections: [
      {
        id: "intro",
        label: "Player Code of Conduct",
        body: [
          { type: "p", text: "As a member of Queens Park SC (QPSC), we expect all players to adhere to the following code of conduct in order to foster a positive and respectful environment within the club. This code outlines the principles and behaviours that reflect the values of our soccer community." },
        ],
      },
      {
        id: "respect",
        label: "1. Respect for Others",
        body: [
          {
            type: "ul",
            items: [
              "a. Treat teammates, coaches, officials, opponents, and supporters with respect at all times.",
              "b. Avoid any form of discrimination, bullying, or harassment based on race, gender, ability, or any other characteristic.",
              "c. Contact between players may occur when training and playing all contact MUST be a football related contact. Any form of violent act must be reported to your coach, and/or Football Department on footballdepartment@queensparksc.com.au",
            ],
          },
        ],
      },
      {
        id: "sportsmanship",
        label: "2. Sportsmanship",
        body: [
          {
            type: "ul",
            items: [
              "a. Display good sportsmanship on and off the field, acknowledging both victory and defeat gracefully.",
              "b. Shake hands with opponents and officials before and after matches.",
            ],
          },
        ],
      },
      {
        id: "commitment",
        label: "3. Commitment and Dedication",
        body: [
          {
            type: "ul",
            items: [
              "a. Attend all scheduled training sessions, matches, and team events unless otherwise communicated to coaches.",
              "b. Give your best effort during training and matches, demonstrating commitment to individual and team improvement.",
            ],
          },
        ],
      },
      {
        id: "punctuality",
        label: "4. Punctuality",
        body: [
          {
            type: "ul",
            items: [
              "a. Arrive on time for all team activities, including training sessions and matches.",
              "b. Communicate promptly with coaches if unable to attend a scheduled event.",
            ],
          },
        ],
      },
      {
        id: "team-unity",
        label: "5. Team Unity",
        body: [
          {
            type: "ul",
            items: [
              "a. Foster a positive and inclusive team environment by supporting and encouraging teammates.",
              "b. Avoid any actions that may disrupt team harmony or cohesion.",
            ],
          },
        ],
      },
      {
        id: "equipment",
        label: "6. Equipment and Facilities",
        body: [
          {
            type: "ul",
            items: [
              "a. Respect club property, including uniforms, equipment, and facilities.",
              "b. Report any damage or issues with equipment to the coaching staff.",
            ],
          },
        ],
      },
      {
        id: "adherence",
        label: "7. Adherence to Club Policies",
        body: [
          {
            type: "ul",
            items: [
              "a. Familiarise yourself with and follow all QPSC policies and guidelines.",
              "b. Seek guidance from coaches or club officials if there are any uncertainties regarding club rules.",
            ],
          },
        ],
      },
      {
        id: "communication",
        label: "8. Communication",
        body: [
          {
            type: "ul",
            items: [
              "a. Maintain open and respectful communication with coaches, teammates, and club officials.",
              "b. Report any concerns or issues to the appropriate club personnel in a timely manner.",
            ],
          },
        ],
      },
      {
        id: "personal-conduct",
        label: "9. Personal Conduct",
        body: [
          {
            type: "ul",
            items: [
              "a. Avoid engaging in any behaviour that may bring disrepute to QPSC, both on and off the field.",
              "b. Uphold a positive image as a representative of the club within the broader community.",
            ],
          },
        ],
      },
      {
        id: "improvement",
        label: "10. Continuous Improvement",
        body: [
          {
            type: "ul",
            items: [
              "a. Demonstrate a commitment to personal and team development by actively seeking feedback and striving for improvement.",
              "b. Embrace opportunities for skill development and learning both on and off the field.",
            ],
          },
        ],
      },
      {
        id: "suspension",
        label: "11. Suspension/Removal",
        body: [
          {
            type: "ul",
            items: [
              "a. The club has the right to suspend or remove a player due to unacceptable behaviours by a player or family member.",
            ],
          },
          { type: "p", text: "By adhering to this Player Code of Conduct, you contribute to the positive and constructive soccer experience for all members of QPSC. Failure to comply with these guidelines may result in disciplinary actions as determined by the club's management. Club Management decisions are FINAL." },
          { type: "p", text: "Note: Your agreement to this Player Code of Conduct is established when submitting your child's registration via the PlayFootball system." },
        ],
      },
    ],
  },
  "refund-policy": {
    slug: "refund-policy",
    category: "terms",
    title: "Refund Policy",
    shortTitle: "Refund",
    version: "Doc.05-02",
    status: "draft",
    effectiveDateLabel: "",
    legalEntity: "Queens Park Soccer Club Inc.",
    legalEntityId: "ABN 35 060 829 551",
    contactEmail: "footballdepartment@queensparksc.com.au",
    sections: [
      {
        id: "intro",
        label: "Refund Policy",
        body: [
          { type: "p", text: "At Queens Park SC (QPSC), we strive to provide the best experience for our members. We understand that circumstances may change, and we aim to accommodate such situations through our refund policy. Please read the following carefully before making any payments." },
        ],
      },
      {
        id: "no-deposit-refund",
        label: "1. No Deposit Refund",
        body: [
          { type: "p", text: "Payments received by QPSC can fall into a number of groups. Such as deposit payment, in full payment, and balance or final payments. Between opening enrolments for a new season and the 31st January you will secure a spot with a team these deposit payments are non-refundable. If member paid in full then the first $300 is non-refundable or $600 if for a JDL team." },
        ],
      },
      {
        id: "partial-refund",
        label: "2. Partial Refund for Unused Membership Period",
        body: [
          { type: "p", text: "If a member is unable to utilise their full membership due to unforeseen circumstances, a partial refund MAY be granted for the unused portion of the membership period (such as relocation, or other personal reasons). The refund amount will be calculated based on the remaining duration of the membership term at the time of the refund request. (We do not hold any jurisdiction over Football West and Football Australia refunds, if you have played any competition games no refund is usually approved)" },
        ],
      },
      {
        id: "refund-procedure",
        label: "3. Refund Procedure",
        body: [
          { type: "p", text: "To request a refund for the unused portion of your membership, Please email the club at footballdepartment@queensparksc.com.au with;" },
          {
            type: "ul",
            items: [
              "1. Subject – Refund Request (Player's Name)",
              {
                text: "2. Provide the following players information;",
                items: [
                  "a. Players full name",
                  "b. Players Date of Birth",
                  "c. Players Team (eg. U7s)",
                ],
              },
              "3. Provide the reason you are requesting a refund.",
            ],
          },
          { type: "p", text: "QPSC Management will use this information to assess your refund request and provide you a response." },
        ],
      },
      {
        id: "refund-eligibility",
        label: "4. Refund Eligibility",
        body: [
          { type: "p", text: "Refunds are subject to approval and may not be granted in certain situations, including but not limited to:" },
          {
            type: "ul",
            items: [
              "Membership termination due to disciplinary actions.",
              "Failure to adhere to club rules and regulations.",
              "Refund requests are made after the 1st May or once 4 games are played whichever occurs first.",
            ],
          },
        ],
      },
      {
        id: "non-transferable",
        label: "5. Non-Transferable",
        body: [
          { type: "p", text: "Refunds are non-transferable and will be issued using a method most suitable for QPSC." },
        ],
      },
      {
        id: "policy-revisions",
        label: "6. Policy Revisions",
        body: [
          { type: "p", text: "QPSC reserves the right to revise or update this refund policy at any time. Any changes will be communicated to members via email or through our official website." },
          { type: "p", text: "If you have any questions or concerns regarding these policies, please don't hesitate to contact us at footballdepartment@queensparksc.com.au" },
        ],
      },
    ],
  },
  mpio: {
    slug: "mpio",
    category: "safety",
    title: "Member Protection Information Officer (MPIO)",
    shortTitle: "Member Protection",
    version: "MPIO-01",
    status: "draft",
    effectiveDateLabel: "",
    legalEntity: "Queens Park Soccer Club Inc.",
    legalEntityId: "ABN 35 060 829 551",
    contactEmail: "mpio@queensparksc.com.au",
    sections: [
      {
        id: "meet-our-mpio",
        label: "Meet our MPIO",
        body: [
          { type: "p", text: "Queens Park SC is pleased to introduce Kirsty F. as our Member Protection Information Officer (MPIO)." },
          { type: "p", text: "As our club continues to grow, we are committed to doing so with quality, responsibility and a strong focus on creating a safe, respectful and welcoming environment for all our players, families, coaches and volunteers." },
          { type: "p", text: "Kirsty's role will help support our members and strengthen the standards and processes we have in place as we continue working towards achieving 3-Star status in the Club Changer Program." },
          { type: "p", text: "Growing our club is important, but how we grow matters even more." },
          { type: "p", text: "Welcome to the role, Kirsty, and thank you for helping us continue to make Queens Park SC a positive and safe place for our entire community." },
          { type: "p", text: "One Club. One Family. One Future." },
        ],
      },
      {
        id: "contact",
        label: "How to contact our MPIO",
        body: [
          { type: "p", text: "If you need to contact our MPIO, please email mpio@queensparksc.com.au." },
        ],
      },
    ],
  },
};
